import '@total-typescript/ts-reset';

import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { ExecutionResult, print } from 'graphql';

export class ContentfulFetchError extends Error {
  name = 'ContentfulFetchError' as const;

  constructor(
    public readonly request: Request,
    public readonly response: Response,
    public readonly responseBody?: unknown,
  ) {
    const statusText = `HTTP ${response.status} ${response.statusText}`;
    const bodyMessage = responseBody ? `\nResponse body: ${JSON.stringify(responseBody, null, 2)}` : '';
    super(`Contentful GraphQL request failed: ${statusText}${bodyMessage}`);
    this.name = 'ContentfulFetchError';
  }
}

type FetcherType = typeof fetch;

export class ContentfulGraphqlClient {
  constructor(
    private readonly credentials: {
      spaceId: string;
      environment: string;
      accessToken: string;
      previewToken: string;
    },
    private readonly defaultFetcher: FetcherType,
  ) {}

  async execute<Result, Variables extends Record<string, unknown>>(
    query: TypedDocumentNode<Result, Variables>,
    variables: Variables,
    options: {
      fetcher?: FetcherType;
    } = {},
  ): Promise<ExecutionResult<Result>> {
    const { fetcher = this.defaultFetcher } = options;
    const isPreview = variables?.preview;
    const request = new Request(
      `https://graphql.contentful.com/content/v1/spaces/${this.credentials.spaceId}/environments/${this.credentials.environment}`,
      {
        method: 'POST',
        headers: {
          'Accept-Encoding': 'gzip',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${isPreview ? this.credentials.previewToken : this.credentials.accessToken}`,
        },
        cache: isPreview ? 'no-cache' : 'default',
        body: JSON.stringify({
          query: print(query),
          variables,
        }),
      },
    );
    const response = await fetcher(request);

    if (!response.ok) {
      let responseBody: unknown;
      try {
        responseBody = await response.json();
      } catch {
        responseBody = await response.text();
      }
      throw new ContentfulFetchError(request, response, responseBody);
    }

    return (await response.json()) as Promise<ExecutionResult<Result>>;
  }
}

export function filterNullable<D>(items: (D | null | undefined)[] | undefined): D[] {
  return items?.filter(Boolean) ?? [];
}
